// =============================================================================
// features/sales/agenda/AgendaContent.tsx — Orquestra cabeçalho, indicadores,
// abas, filtros e lista da agenda comercial (doc 26 §6.1). Montado só depois
// que o gate de permissão+flag (AgendaPage.tsx) libera.
// =============================================================================

import type { AppointmentStatus, ContactChannel } from '@elemento/shared-schemas';
import * as React from 'react';

import { useAssignableAgents } from '../../../hooks/admin/useAgents';
import { useAppointments } from '../../../hooks/sales/useAppointments';
import { useAuth } from '../../../lib/auth-store';

import { AgendaFilters } from './AgendaFilters';
import { AgendaHeader } from './AgendaHeader';
import type { AgendaViewMode } from './AgendaHeader';
import { AgendaIndicators } from './AgendaIndicators';
import { AgendaTabs } from './AgendaTabs';
import { AppointmentList } from './AppointmentList';
import type { AgendaTab } from './buckets';
import { windowForTab } from './buckets';
import type { CampaignFilter, SituacaoFilter } from './filters';
import { applyCampaignFilter } from './filters';

const PAGE_SIZE = 20;
/** "Agora" primeiro — a tela é o início do dia do vendedor (doc 26 §6.1 Objetivo). */
const DEFAULT_TAB: AgendaTab = 'agora';
/** Atualiza `now` periodicamente para os badges de urgência não ficarem parados. */
const CLOCK_TICK_MS = 60_000;

export function AgendaContent(): React.JSX.Element {
  const { hasPermission } = useAuth();
  const canFilterByAgent = hasPermission('sales_appointments:manage');

  const [now, setNow] = React.useState(() => new Date());
  React.useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), CLOCK_TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  const [tab, setTab] = React.useState<AgendaTab>(DEFAULT_TAB);
  const [viewMode, setViewMode] = React.useState<AgendaViewMode>('lista');
  const [channel, setChannel] = React.useState<ContactChannel | 'all'>('all');
  const [campaignFilter, setCampaignFilter] = React.useState<CampaignFilter>('all');
  const [situacaoFilter, setSituacaoFilter] = React.useState<SituacaoFilter>('all');
  const [assignedAgentId, setAssignedAgentId] = React.useState<string | 'all'>('all');
  const [offset, setOffset] = React.useState(0);

  // Toda troca de aba/filtro reseta a paginação — página 1 sempre reflete o novo recorte.
  const withReset =
    <T,>(setter: React.Dispatch<React.SetStateAction<T>>) =>
    (value: T): void => {
      setter(value);
      setOffset(0);
    };

  const { agents } = useAssignableAgents();
  const agentOptions = agents.map((a) => ({ value: a.id, label: a.display_name }));
  const agentNameById = new Map(agents.map((a) => [a.id, a.display_name]));

  const windowFilters = windowForTab(tab, now);
  // Situação (Concluído/Cancelado) vai para o servidor — a API já filtra por
  // `status` (packages/shared-schemas/sales-appointments.ts), então restringir
  // o array aqui faz a PAGINAÇÃO refletir o filtro real, em vez de reduzir só
  // a página já carregada (bug: usuário preso numa página "vazia por filtro"
  // enquanto outra página do servidor tinha resultados — achado de revisão
  // F33-S05). Campanha continua client-side (ver nota em filters.ts: a API só
  // filtra por campaignMemberId exato, não existe "tem campanha" no servidor).
  const statusOverride: AppointmentStatus[] | undefined =
    tab === 'concluidos' && situacaoFilter !== 'all' ? [situacaoFilter] : undefined;

  const { data, isLoading, isError, refetch } = useAppointments({
    ...windowFilters,
    ...(statusOverride ? { status: statusOverride } : {}),
    ...(channel !== 'all' ? { channel: [channel] } : {}),
    ...(assignedAgentId !== 'all' ? { assignedAgentId: [assignedAgentId] } : {}),
    limit: PAGE_SIZE,
    offset,
  });

  const visible = applyCampaignFilter(data?.data ?? [], campaignFilter);

  const hasExtraFilters =
    channel !== 'all' ||
    assignedAgentId !== 'all' ||
    campaignFilter !== 'all' ||
    (tab === 'concluidos' && situacaoFilter !== 'all');

  const clearFilters = (): void => {
    setChannel('all');
    setCampaignFilter('all');
    setSituacaoFilter('all');
    setAssignedAgentId('all');
    setOffset(0);
  };

  return (
    <div className="flex flex-col gap-6">
      <AgendaHeader now={now} viewMode={viewMode} onViewModeChange={setViewMode} />

      <AgendaIndicators now={now} enabled />

      <div className="flex flex-col gap-4">
        <AgendaTabs value={tab} onChange={withReset(setTab)} />

        <AgendaFilters
          tab={tab}
          channel={channel}
          onChannelChange={withReset(setChannel)}
          campaignFilter={campaignFilter}
          onCampaignFilterChange={withReset(setCampaignFilter)}
          situacaoFilter={situacaoFilter}
          onSituacaoFilterChange={withReset(setSituacaoFilter)}
          canFilterByAgent={canFilterByAgent}
          assignedAgentId={assignedAgentId}
          onAssignedAgentChange={withReset(setAssignedAgentId)}
          agentOptions={agentOptions}
        />
      </div>

      <AppointmentList
        items={visible}
        now={now}
        isLoading={isLoading}
        isError={isError}
        hasExtraFilters={hasExtraFilters}
        viewMode={viewMode}
        onRetry={() => void refetch()}
        onClearFilters={clearFilters}
        agentNameById={agentNameById}
        showAgentName={canFilterByAgent}
        offset={offset}
        limit={data?.limit ?? PAGE_SIZE}
        total={data?.total ?? 0}
        onOffsetChange={setOffset}
      />
    </div>
  );
}
